<template>
    <div class="w-full flex flex-col items-start relative">
        <div class="w-full mt-1 flex items-stretch">
            <div :class="[hideLateralDescriptionValue ? 'w-full' : 'w-1/2 pr-2', inputClass]">
                <Label
                    :for="config.display_name"
                    :class="['mb-2', overrideLabelOpacity ? 'override-opacity' : '']"
                >
                    {{ config.display_name }}
                </Label>

                <div class="relative">
                    <div class="mt-1 w-full border border-border rounded-md bg-card" :style="{ height: editorHeight }">
                        <VueMonacoEditor
                            :key="editorUid"
                            v-model:value="code"
                            :language="activeLangId"
                            :theme="effectiveThemeId"
                            :path="editorPath"
                            :options="editorOptions"
                            @beforeMount="onEditorBeforeMount"
                            @mount="onEditorMount"
                            class="w-full h-full" />
                    </div>
                    <!-- Tooltip positioned inside editor area -->
                    <Tooltip v-if="props.config.description && hideLateralDescriptionValue">
                        <TooltipTrigger asChild>
                            <span class="absolute right-2 top-2 flex items-center justify-center z-10">
                                <Info class="h-4 w-4 text-muted-foreground" />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent side="left" class="whitespace-normal max-w-xs">
                            <p class="text-xs" v-html="sanitizeHtml(props.config.description)" />
                        </TooltipContent>
                    </Tooltip>
                </div>

                <div v-if="errors.length" class="text-destructive mt-1 text-xs">
                    <template v-for="err in errors" :key="err.$uid">
                        <span>· {{ err.$message }}</span
                        ><br />
                    </template>
                </div>
            </div>
            <div v-if="!hideLateralDescriptionValue" class="w-1/2 pl-2">
                <!-- lateral description panel -->
                <Alert
                    v-if="props.config.description"
                    variant="default"
                    class="h-full flex items-center p-3 text-xs border bg-muted/10">
                    <AlertDescription class="text-muted-foreground text-2xs">
                        <span v-html="sanitizeHtml(props.config.description)" />
                    </AlertDescription>
                </Alert>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted, onUpdated } from 'vue';
import { VueMonacoEditor } from '@guolao/vue-monaco-editor';
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-vue-next';
import _ from 'lodash';
import { useMonacoTheme } from '@/composables/useMonacoTheme';
import { sanitizeHtml } from '@/utils/utils';

// Predefined custom languages
const PREDEFINED_LANGS = {
    dbpath: {
        id: 'dbpath',
        monarch: {
            defaultToken: '',
            tokenizer: {
                root: [
                    [/[A-Za-z_]\w*/, 'source'],
                    [/\./, 'delimiter'],
                    [/:/, { token: 'colon', next: '@destination' }]
                ],
                destination: [
                    [/[A-Za-z_]\w*/, 'dest'],
                    [/\./, 'delimiter']
                ]
            }
        },
        themeRules: [
            { token: 'source', foreground: '008800', fontStyle: 'bold' },
            { token: 'dest', foreground: '000088', fontStyle: 'bold' },
            { token: 'delimiter', foreground: '444444' },
            { token: 'colon', foreground: '880000', fontStyle: 'italic' }
        ]
    },
    network: {
        id: 'network',
        monarch: { defaultToken: '', tokenizer: { root: [[/(?:\d{1,3}\.){3}\d{1,3}:\d+/, 'ipPort']] } },
        themeRules: [{ token: 'ipPort', foreground: '880000', fontStyle: 'italic' }]
    },
    pathmap: {
        id: 'pathmap',
        monarch: { defaultToken: '', tokenizer: { root: [[/[A-Za-z_]\w*=[A-Za-z_]\w*/, 'keyVal']] } },
        themeRules: [{ token: 'keyVal', foreground: '444400' }]
    },
    sqlref: {
        id: 'sqlref',
        monarch: {
            defaultToken: '',
            tokenizer: { root: [[/[A-Za-z_]\w*\.[A-Za-z_]\w*/, 'tableCol']] }
        },
        themeRules: [{ token: 'tableCol', foreground: '0055AA', fontStyle: 'underline' }]
    }
};

// Props & emits
const props = defineProps({
    config: { type: Object, required: true },
    value: { type: [String, null], default: null },
    editMode: { type: Boolean, default: true },
    overrideLabelOpacity: { type: Boolean, default: false },
    hideLateralDescription: { type: Boolean, default: false },
    errors: { type: Array, default: () => [] },
    inputClass: { type: String, default: '' }
});
const emit = defineEmits(['input']);

// Editor content
const code = ref(_.isString(props.value) ? props.value : props.config.value.default || '');
watch(
    () => props.value,
    (v) => (code.value = v)
);
// Store validation state for JSON schema
const isValidJson = ref(true);

// Update emit to handle validation state
const emitChange = (value, valid = null) => {
    if (valid !== null) {
        isValidJson.value = valid;
    }
    emit('input', value, isValidJson.value);
};

watch(code, (v) => {
    if (activeLangId === 'json' && cfg.schema) {
        // For JSON with schema, emit value change but don't override validation status
        // The validation status will be set by Monaco's checkValidation function
        emit('input', v, isValidJson.value);
    } else {
        // For other languages, emit normally
        emitChange(v);
    }
});

// Sidebar visibility - consistent with other fields
const hideLateralDescriptionValue = ref(props.hideLateralDescription);

onUpdated(() => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : !props.editMode || window.innerWidth <= 1024;
});

const handleResize = () => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : window.innerWidth <= 1024;
};

onMounted(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

// Height
const DEFAULT_LINES = 10;
const LINE_HEIGHT_PX = 18;
const editorHeight = computed(() => {
    const v = props.config.value;
    if (v.heightPx != null) return v.heightPx + 'px';
    const lines = v.heightLines || DEFAULT_LINES;
    return lines * LINE_HEIGHT_PX + 'px';
});

// Theme management
const { monacoTheme } = useMonacoTheme();

// Configuration
const cfg = props.config.value;
// ensure unique model path and component key so multiple editors don't share models
const editorUid = _.uniqueId('codeEditor_');
const editorPath = computed(() => {
    // For JSON with schema, use a .json extension to help Monaco identify the file type
    if (cfg.language === 'json' && cfg.schema) {
        return `${cfg.path || editorUid}.json`;
    }
    return cfg.path ? `${cfg.path}_${editorUid}` : editorUid;
});
let custom = cfg.customLangDefinition || (cfg.customLangKey && PREDEFINED_LANGS[cfg.customLangKey]);
const activeLangId = custom?.id || cfg.language || 'javascript';
const effectiveThemeId = computed(() => custom?.themeRules ? `${custom.id}-theme` : monacoTheme.value);
const editorOptions = {
    // Default options for JSON
    ...(cfg.language === 'json' ? {
        formatOnType: true,
        formatOnPaste: true,
        autoIndent: 'full',
        tabSize: 2,
        insertSpaces: true
    } : {}),
    // User-defined options override defaults
    ...(cfg.options || {})
};

// Validators
const validators = [];
if (typeof cfg.validatorDefinition === 'function') validators.push(cfg.validatorDefinition);
if (cfg.validation?.regex) {
    const { regex: pattern, flags = '', error_message: errorMessage = 'Invalid format.', severity = monaco.MarkerSeverity.Error } = cfg.validation;
    let rx;
    try {
        rx = new RegExp(pattern, flags);
    } catch {
        rx = null;
    }
    if (rx) {
        validators.push((codeVal) =>
            rx.test(codeVal) ? [] : [{ severity, message: errorMessage, startLineNumber: 1, startColumn: 1, endLineNumber: 1, endColumn: 1 }]
        );
    }
}

// Before Monaco initialises: register custom languages & themes + JSON schema
function onEditorBeforeMount(monacoInstance) {
    // Handle custom language registration
    if (custom) {
        monacoInstance.languages.register({ id: custom.id });
        monacoInstance.languages.setMonarchTokensProvider(custom.id, custom.monarch);
        if (custom.themeRules) {
            monacoInstance.editor.defineTheme(`${custom.id}-theme`, {
                base: monacoTheme.value,
                inherit: true,
                rules: custom.themeRules,
                colors: {}
            });
        }
    }
    
    // Handle JSON schema configuration
    if (activeLangId === 'json' && cfg.schema) {
        const schemaUri = `http://json-schema/${editorUid}`;
        monacoInstance.languages.json.jsonDefaults.setDiagnosticsOptions({
            validate: true,
            allowComments: false,
            schemaValidation: 'error',
            schemas: [{
                uri: schemaUri,
                fileMatch: [`*${editorUid}.json`],
                schema: cfg.schema
            }]
        });
    }
}

// After mount: set language & apply diagnostics
function onEditorMount(editor, monacoInstance) {
    const model = editor.getModel();
    
    // Set language for custom languages
    if (custom && model) {
        monacoInstance.editor.setModelLanguage(model, custom.id);
    }
    
    // For JSON with schema, ensure model URI matches schema configuration
    if (activeLangId === 'json' && cfg.schema && model) {
        const uri = monacoInstance.Uri.parse(`inmemory://model/${editorUid}.json`);
        // Dispose old model and create new one with correct URI for schema matching
        const content = editor.getValue();
        const newModel = monacoInstance.editor.createModel(content, 'json', uri);
        editor.setModel(newModel);
        model.dispose();
        
        // Set up validation checking for JSON with schema
        const checkValidation = () => {
            const markers = monacoInstance.editor.getModelMarkers({ resource: uri });
            const hasErrors = markers.some(marker =>
                marker.severity === monacoInstance.MarkerSeverity.Error ||
                marker.severity === monacoInstance.MarkerSeverity.Warning
            );
            
            // Update validation status
            const isValid = !hasErrors;
            isValidJson.value = isValid;
            emit('input', code.value, isValid);
        };
        
        // Check validation on content change
        editor.onDidChangeModelContent(() => {
            setTimeout(checkValidation, 100); // Increased delay to allow Monaco to process
            setTimeout(checkValidation, 300); // Additional check for slower validations
        });
        
        // Listen for marker changes directly
        monacoInstance.editor.onDidChangeMarkers(([resource]) => {
            if (resource.toString() === uri.toString()) {
                checkValidation();
            }
        });
        
        // Initial validation check
        setTimeout(checkValidation, 200);
        setTimeout(checkValidation, 500); // Additional initial check
    }
    
    // Apply custom validators (for non-JSON schema or additional validation)
    if (validators.length && (!cfg.schema || activeLangId !== 'json')) {
        const runValidators = () => {
            let markers = [];
            for (const fn of validators) markers = markers.concat(fn(editor.getValue()) || []);
            monacoInstance.editor.setModelMarkers(editor.getModel(), activeLangId, markers);
        };
        editor.onDidChangeModelContent(runValidators);
        runValidators();
    }
}

function onEditorChange(val) {
    // Additional change logic if required
}

// Example configurations:

// 1. Custom language with regex validation (existing functionality)
// const customLangExample = {
//     config: {
//         display_name: 'SQL Mapping',
//         description: 'Write your `schema.table.column:dest.schema.table` mappings here.',
//         value: {
//             path: 'mappings/config.txt',
//             language: 'plaintext',
//             customLangKey: 'dbpath',
//             validation: {
//                 regex: '^[A-Za-z0-9_]+(?:\\.[A-Za-z0-9_]+){1,2}(?::[A-Za-z0-9_]+(?:\\.[A-Za-z0-9_]+){1,2})?$',
//                 error_message: 'Must be `schema.table.column[:schema.table.column]` format'
//             }
//         }
//     }
// };

// 2. JSON with schema validation (new functionality)
// const jsonSchemaExample = {
//     config: {
//         display_name: 'Rename Columns',
//         description: 'JSON mapping of source to target column names.',
//         value: {
//             language: 'json',
//             schema: {
//                 type: 'object',
//                 patternProperties: {
//                     '^[a-zA-Z0-9_]+(\\.[a-zA-Z0-9_]+){0,2}$': {
//                         type: 'string',
//                         pattern: '^[a-zA-Z_][a-zA-Z0-9_]*$'
//                     }
//                 },
//                 additionalProperties: false
//             },
//             default: '{\n  "public.orders.amount": "Amount"\n}'
//         }
//     }
// };
</script>

